/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/destructuring-assignment */
import {
  Badge,
  Grid,
  Group,
  Input,
  NumberInput,
  Select,
  SimpleGrid,
  Space,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconBuilding, IconCalendar, IconInfoCircle, IconNotes, IconReceipt2 } from '@tabler/icons-react';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { ProposalTypeEnum } from '../../../../../../business/proposals/status';
import EntityContactPicker from '../../../../../../components/core/EntityContactPicker/EntityContactPicker';
import EntityPicker from '../../../../../../components/core/EntityPicker/EntityPicker';
import PageSection from '../../../../../../components/core/PageSection/PageSection';
import { useProposalGeneralContext } from '../../../../../../contexts/core/proposals/ProposalGeneral.context';
import useCurrentUser from '../../../../../../hooks/useCurrentUser';
import { ClosingType, CompanyType, EntityTypeType } from '../../../../../../models/core/cache.type';
import { Action } from '../../../../../../models/core/core.type';
import { EntityContactType, EntitySearchResponseType } from '../../../../../../models/core/entities.type';
import { ProposalType } from '../../../../../../models/core/proposals.type';
import theme from '../../../../../../theme';
import { Feature } from '../../../../../../utils/constants.utils';
import { formatDateStringToDate, formatDateToString } from '../../../../../../utils/formatter.utils';
import { buildFakeAuditObject, tryGetDateISOFormat } from '../../../../../../utils/helper.utils';

type FormViewData = {
  action: Action;
  idEmpresa: string;
  idProposta: number | null;
  codigoFechamento: string;

  clienteData: EntitySearchResponseType | null;
  idCliente: number | null;
  cliente: string;

  contatoData: EntityContactType | null;
  idEntidadeContato: number | null;
  contato: string;

  gerenciadorData: EntitySearchResponseType | null;
  idGerenciador: number | null;
  gerenciador: string;

  dataInicial: Date;
  dataFinal: Date;
  documentacaoClienteRecorrenciaDias: number | null;
  documentacaoCliente: string | null;
  validadeLembreteDias: number | null;
  validade: Date;
  observacao: string | null;
};

type ProposalFormViewProps = {
  referenceData: {
    companies: CompanyType[];
    closingTypeData: ClosingType[];
    entityTypeData: EntityTypeType[];
  };
  proposal: ProposalType | null;
  zIndex: number;
};

const GeneralFormView = forwardRef((props: ProposalFormViewProps, ref) => {
  const [currentUser] = useCurrentUser();
  const { setProposalGeneralData } = useProposalGeneralContext();
  const disabled = props.proposal?.codigoPropostaTipo === ProposalTypeEnum.Reajuste;

  const buildClienteDataFromItem = (item: ProposalType | null): EntitySearchResponseType | null => {
    if (!item) {
      return null;
    }
    if ((item as any).clienteData) {
      return (item as any).clienteData;
    }
    return {
      ...buildFakeAuditObject(),
      idEntidade: Number(item.idCliente),
      tipos: [],
      cnpj: item.clienteCNPJ || null,
      razaoSocial: item.clienteRazaoSocial || null,
      nomeFantasia: item.clienteNomeFantasia || null,
      incricaoEstadual: null,
      incricaoMunicipal: null,
      cpf: item.clienteCPF || null,
      nome: item.clienteNome || null,
      webSite: null,
      aceitaEncontroContas: false,
    };
  };

  const buildContatoDataFromItem = (item: ProposalType | null): EntityContactType | null => {
    if (!item) {
      return null;
    }
    if ((item as any).contatoData) {
      return (item as any).contatoData;
    }
    return {
      ...buildFakeAuditObject(),
      idEntidadeContato: item.idEntidadeContato,
      idEntidade: item.idCliente,
      nome: item.nome,
      telefone: item.telefone || null,
      email: item.email || null,
    };
  };

  const buildGerenciadorDataFromItem = (item: ProposalType | null): EntitySearchResponseType | null => {
    if (!item) {
      return null;
    }
    if ((item as any).gerenciadorData) {
      return (item as any).gerenciadorData;
    }
    return {
      ...buildFakeAuditObject(),
      idEntidade: Number(item.idGerenciador),
      tipos: [],
      cnpj: item.gerenciadorCNPJ || null,
      razaoSocial: item.gerenciadorRazaoSocial || null,
      nomeFantasia: item.gerenciadorNomeFantasia || null,
      incricaoEstadual: null,
      incricaoMunicipal: null,
      cpf: item.gerenciadorCPF || null,
      nome: item.gerenciadorNome || null,
      webSite: null,
      aceitaEncontroContas: false,
    };
  };

  const form = useForm<FormViewData>({
    initialValues: {
      action: props.proposal?.idProposta ? Action.Nothing : Action.Add,
      idEmpresa: props.proposal?.idEmpresa?.toString() || currentUser.idEmpresa.toString() || '',
      idProposta: props.proposal?.idProposta || null,

      clienteData: buildClienteDataFromItem(props.proposal),
      idCliente: props.proposal?.idCliente || null,
      cliente:
        (props.proposal as any)?.clienteData?.nomeFantasia ||
        (props.proposal as any)?.clienteData?.razaoSocial ||
        (props.proposal as any)?.clienteData?.nome ||
        props.proposal?.clienteNomeFantasia ||
        props.proposal?.clienteRazaoSocial ||
        props.proposal?.clienteNome ||
        '',

      contatoData: buildContatoDataFromItem(props.proposal),
      idEntidadeContato: props.proposal?.idEntidadeContato || null,
      contato:
        props.proposal?.idEntidadeContato || (props.proposal as any)?.contatoData?.idEntidadeContato
          ? `${(props.proposal as any)?.contatoData?.nome || props.proposal?.nome} (${
              (props.proposal as any)?.contatoData?.telefone ||
              props.proposal?.telefone ||
              props.proposal?.celular ||
              (props.proposal as any)?.contatoData?.email ||
              props.proposal?.email ||
              '-'
            })`
          : '',

      gerenciadorData: buildGerenciadorDataFromItem(props.proposal),
      idGerenciador: props.proposal?.idGerenciador || null,
      gerenciador:
        (props.proposal as any)?.gerenciadorData?.nomeFantasia ||
        (props.proposal as any)?.gerenciadorData?.razaoSocial ||
        (props.proposal as any)?.gerenciadorData?.nome ||
        props.proposal?.gerenciadorNomeFantasia ||
        props.proposal?.gerenciadorRazaoSocial ||
        props.proposal?.gerenciadorNome ||
        '',

      codigoFechamento: props.proposal?.codigoFechamento || '',

      dataInicial: props.proposal
        ? formatDateStringToDate(tryGetDateISOFormat(props.proposal.dataInicial))
        : new Date(),

      dataFinal: props.proposal
        ? formatDateStringToDate(tryGetDateISOFormat(props.proposal.dataFinal))
        : new Date(),

      documentacaoClienteRecorrenciaDias: props.proposal?.documentacaoClienteRecorrenciaDias || null,
      documentacaoCliente: props.proposal?.documentacaoCliente || '',

      validadeLembreteDias: props.proposal?.validadeLembreteDias || null,
      validade: props.proposal
        ? formatDateStringToDate(tryGetDateISOFormat(props.proposal.validade))
        : new Date(),

      observacao: props.proposal?.observacao || '',
    },
    validate: {
      idEmpresa: (value) => {
        return props.referenceData.companies.find((x) => x.idEmpresa === Number(value))
          ? null
          : 'Campo obrigatório';
      },
      idCliente: (value) => {
        return !value || value <= 0 ? 'Campo obrigatório' : null;
      },
      idEntidadeContato: (value) => {
        return !value || value <= 0 ? 'Campo obrigatório' : null;
      },
      codigoFechamento: (value) => {
        return props.referenceData.closingTypeData.find((x) => x.codigoFechamento === value)
          ? null
          : 'Campo obrigatório';
      },
      dataInicial: (value) => {
        if (Math.abs(value.getFullYear() - new Date().getFullYear()) > 200) {
          return 'Data não suportada';
        }
        return null;
      },
      dataFinal: (value, values: FormViewData) => {
        if (Math.abs(value.getFullYear() - new Date().getFullYear()) > 200) {
          return 'Data não suportada';
        }
        if (value < values.dataInicial) {
          return 'Data final não pode ser anterior à data inicial';
        }
        return null;
      },
      validade: (value) => {
        if (Math.abs(value.getFullYear() - new Date().getFullYear()) > 200) {
          return 'Data não suportada';
        }
        return null;
      },
    },
  });

  const isModified = (): boolean => {
    const origData = `${props.proposal?.idEmpresa || ''}
    |${props.proposal?.idCliente || ''}
    |${props.proposal?.idEntidadeContato || ''}
    |${props.proposal?.idGerenciador || ''}
    |${props.proposal?.codigoFechamento || ''}
    |${formatDateToString(props.proposal?.dataInicial || new Date()) || ''}
    |${formatDateToString(props.proposal?.dataFinal || new Date()) || ''}
    |${formatDateToString(props.proposal?.validade || new Date()) || ''}
    |${props.proposal?.validadeLembreteDias || ''}
    |${props.proposal?.documentacaoClienteRecorrenciaDias || ''}
    |${props.proposal?.documentacaoCliente || ''}
    |${props.proposal?.observacao || ''}`;

    const formData = `${form.values.idEmpresa || ''}
    |${form.values.idCliente || ''}
    |${form.values.idEntidadeContato || ''}
    |${form.values.idGerenciador || ''}
    |${form.values.codigoFechamento || ''}
    |${formatDateToString(form.values.dataInicial) || new Date()}
    |${formatDateToString(form.values.dataFinal) || new Date()}
    |${formatDateToString(form.values.validade) || new Date()}
    |${form.values.validadeLembreteDias || ''}
    |${form.values.documentacaoClienteRecorrenciaDias || ''}
    |${form.values.documentacaoCliente || ''}
    |${form.values.observacao || ''}`;

    return origData !== formData;
  };

  useImperativeHandle(ref, () => ({
    validate(): FormViewData | null {
      if (form.validate().hasErrors) {
        throw Error('Existem pendências a serem corrigidas.');
      }
      const formItem = JSON.parse(JSON.stringify(form.values)) as FormViewData;

      if (props.proposal?.idProposta && isModified()) {
        formItem.action = Action.Modify;
      }

      // picker
      formItem.idCliente = Number(formItem.idCliente);
      formItem.idEntidadeContato = Number(formItem.idEntidadeContato);
      if (formItem.idGerenciador) {
        formItem.idGerenciador = Number(formItem.idGerenciador);
      }

      formItem.documentacaoCliente = formItem.documentacaoCliente?.trim() || null;
      formItem.observacao = formItem.observacao?.trim() || null;

      // Leave this commented out. To be used by the draft.
      // delete (formItem as any).clienteData;

      return formItem;
    },
    clear() {
      form.reset();
      form.setFieldValue('idCliente', -1);
      form.setFieldValue('cliente', '');
      form.setFieldValue('idEntidadeContato', -1);
      form.setFieldValue('contato', '');
      form.setFieldValue('idGerenciador', -1);
      form.setFieldValue('gerenciador', '');
      form.setFieldValue('documentacaoClienteRecorrenciaDias', 0);
    },
  }));

  useEffect(() => {
    setProposalGeneralData({
      cliente: buildClienteDataFromItem(props.proposal),
      codigoFechamento: props.proposal?.codigoFechamento || null,
      dataInicial: props.proposal?.dataInicial || new Date(),
      dataFinal: props.proposal?.dataFinal || new Date(),
      servicos: props.proposal?.servicos || [],
    });
  }, []);

  useEffect(() => {
    setProposalGeneralData({
      cliente: form.values.clienteData,
      codigoFechamento: form.values.codigoFechamento,
      dataInicial: form.values.dataInicial,
      dataFinal: form.values.dataFinal,
      servicos: props.proposal?.servicos || [],
    });
  }, [form.values, setProposalGeneralData]);

  return (
    <div>
      <Group justify="space-between">
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Geral"
          text="Informações gerais da proposta como cliente, fechamento, datas, documentação, etc."
        />
        <Badge variant="outline">{props.proposal?.propostaTipo || 'Completa'}</Badge>
      </Group>
      <Space h="xs" />

      <Grid columns={8}>
        <Grid.Col span={1}>
          <Select
            leftSection={<IconBuilding size={15} />}
            label="Empresa"
            placeholder="Selecione..."
            data={props.referenceData.companies.map((x) => {
              return {
                value: x.idEmpresa.toString(),
                label: x.empresa,
                disabled: x.idEmpresa === 0,
              };
            })}
            searchable
            required
            disabled={!currentUser.executivo || disabled}
            {...form.getInputProps('idEmpresa')}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <EntityPicker
            label="Cliente"
            description={null}
            formItem={form.getInputProps('idCliente')}
            idsToBeDisabled={[]}
            disabled={disabled}
            required
            callback={(item: EntitySearchResponseType | EntitySearchResponseType[] | null) => {
              if (!Array.isArray(item) && item) {
                form.setFieldValue('clienteData', item);
                form.setFieldValue('idCliente', item.idEntidade);
                form.setFieldValue('cliente', item.nomeFantasia || item.razaoSocial || item.nome || '');
                form.setFieldValue('idEntidadeContato', -1);
                form.setFieldValue('contato', '');
              }
            }}
            zIndex={props.zIndex + 1}
            useCache
            allowMultiple={false}
            query={undefined}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <EntityContactPicker
            key={form.values.idCliente}
            label="Contato"
            idEntidade={form.values.idCliente!}
            formItem={form.getInputProps('idEntidadeContato')}
            idsToBeDisabled={[]}
            disabled={!form.values.idCliente}
            callback={async (item: EntityContactType | null) => {
              if (item) {
                form.setFieldValue('contatoData', item);
                form.setFieldValue('idEntidadeContato', item.idEntidadeContato!);
                form.setFieldValue(
                  'contato',
                  `${item.nome} (${item.telefone || item.celular || item.email || '-'})`
                );
              }
            }}
            zIndex={props.zIndex + 1}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <EntityPicker
            label="Gerenciador"
            description={null}
            formItem={form.getInputProps('idGerenciador')}
            idsToBeDisabled={[]}
            disabled={false}
            required={false}
            callback={(item: EntitySearchResponseType | EntitySearchResponseType[] | null) => {
              if (!Array.isArray(item)) {
                form.setFieldValue('gerenciadorData', item);
                form.setFieldValue('idGerenciador', item?.idEntidade || null);
                form.setFieldValue(
                  'gerenciador',
                  item?.nomeFantasia || item?.razaoSocial || item?.nome || ''
                );
              }
            }}
            zIndex={props.zIndex + 1}
            useCache
            allowMultiple={false}
            query={undefined}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <Select
            leftSection={<IconReceipt2 size={15} />}
            label="Fechamento"
            placeholder="Selecione..."
            data={props.referenceData.closingTypeData.map((x) => {
              return {
                value: x.codigoFechamento,
                label: x.fechamento,
              };
            })}
            required
            // disabled={disabled}
            {...form.getInputProps('codigoFechamento')}
          />
        </Grid.Col>
      </Grid>
      <Space h="xs" />

      <SimpleGrid cols={4}>
        <div>
          <Input.Wrapper label="Data Inicial" required>
            <Group
              style={{
                display: 'inline-block',
                marginLeft: 5,
                marginTop: -2,
                verticalAlign: 'bottom',
              }}
            >
              <Tooltip
                withArrow
                transitionProps={{ transition: 'fade', duration: 200 }}
                label="Data prevista de início do contrato"
              >
                <div>
                  <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                </div>
              </Tooltip>
            </Group>
          </Input.Wrapper>
          <DateInput
            leftSection={<IconCalendar size={15} />}
            clearable={false}
            required
            {...form.getInputProps('dataInicial')}
          />
        </div>
        <div>
          <Input.Wrapper label="Data Final" required>
            <Group
              style={{
                display: 'inline-block',
                marginLeft: 5,
                marginTop: -2,
                verticalAlign: 'bottom',
              }}
            >
              <Tooltip
                withArrow
                transitionProps={{ transition: 'fade', duration: 200 }}
                label="Data prevista de fim do contrato"
              >
                <div>
                  <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                </div>
              </Tooltip>
            </Group>
          </Input.Wrapper>
          <DateInput
            leftSection={<IconCalendar size={15} />}
            clearable={false}
            minDate={form.values.dataInicial}
            required
            {...form.getInputProps('dataFinal')}
          />
        </div>
        <DateInput
          leftSection={<IconCalendar size={15} />}
          label="Validade"
          clearable={false}
          required
          {...form.getInputProps('validade')}
        />
        <NumberInput
          label="Lembrete da Validade"
          placeholder="Lembrete em dias"
          min={1}
          max={3120}
          {...form.getInputProps('validadeLembreteDias')}
        />
      </SimpleGrid>
      <Space h="xs" />

      <Grid columns={4}>
        <Grid.Col span={1}>
          <NumberInput
            label="Recorrência de Envio da Documentação"
            placeholder="Recorrência em dias"
            min={1}
            max={3120}
            // disabled={disabled}
            {...form.getInputProps('documentacaoClienteRecorrenciaDias')}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Textarea
            leftSection={<IconNotes size={15} />}
            label="Documentação"
            placeholder="Detalhes da documentacao"
            autosize
            maxLength={500}
            // disabled={disabled}
            {...form.getInputProps('documentacaoCliente')}
          />
        </Grid.Col>
      </Grid>
      <Space h="xs" />

      <SimpleGrid cols={1}>
        <Textarea
          leftSection={<IconNotes size={15} />}
          label="Observação"
          placeholder="Observação sobre a proposta"
          autosize
          maxLength={500}
          // disabled={disabled}
          {...form.getInputProps('observacao')}
        />
      </SimpleGrid>
    </div>
  );
});

export default GeneralFormView;
